/**
 * Dealer's MUI theme: the shared portal theme from `@hopdrive/sdk-theme`, plus
 * a keyboard focus ring.
 *
 * `buildPortalTheme` sets `MuiButton.defaultProps` to the contained variant with
 * elevation off, which cancels the contained variant's only focus affordance,
 * so the ring has to be laid over the built theme. `focusVisibleComponents`
 * takes the running palette to work out which contained fills the black ink
 * cannot carry.
 *
 * The ring is spread into each component's `styleOverrides.root` by hand rather
 * than through `createTheme(base, overrides)`: the portal theme's `MuiButton`
 * root is an `({ ownerState }) =>` callback returning one of three heights, and
 * MUI's deep merge would replace that callback with the ring object and drop the
 * heights. Spreading the ring block last inside the callback keeps the heights
 * and puts `&.Mui-focusVisible` after them, which a specificity tie needs.
 *
 * Dealer renders the shared theme unbranded (customer branding is an embed
 * concern) and has no dark mode: one frozen theme object goes to the provider.
 */
import type { Theme } from '@mui/material/styles';
import { buildPortalTheme } from '@hopdrive/sdk-theme';
import { focusVisibleComponents, type FocusRingPalette } from '@hopdrive/sdk-ui';

// sdk-theme resolves its own copy of @mui/material under pnpm, and the two
// copies' Theme types are not assignable to each other even though the objects
// are identical in shape. One cast at the boundary keeps the rest of this file
// on the app's own Theme type.
const base = buildPortalTheme('light', { app: 'dealer' }) as unknown as Theme;

const ringPalette: FocusRingPalette = {
  primary: base.palette.primary,
  secondary: base.palette.secondary,
  error: base.palette.error,
  warning: base.palette.warning,
  info: base.palette.info,
  success: base.palette.success,
  background: { paper: base.palette.background.paper, default: base.palette.background.default },
};

const focusVisible = focusVisibleComponents('light', ringPalette);

type RootOverride = Record<string, unknown> | ((props: never) => Record<string, unknown>);

/** Works whether the base root override is an object or a callback. */
function ringed(baseRoot: RootOverride | undefined, ring: Record<string, unknown>): RootOverride {
  if (typeof baseRoot === 'function') {
    return (props: never) => ({ ...baseRoot(props), ...ring });
  }
  return { ...(baseRoot ?? {}), ...ring };
}

function withFocusRing(components: Theme['components']): Theme['components'] {
  const merged: Record<string, { styleOverrides?: { root?: RootOverride } & Record<string, unknown> } & Record<string, unknown>> = {
    ...(components as Record<string, never> | undefined),
  };
  for (const [name, override] of Object.entries(focusVisible)) {
    const existing = merged[name] ?? {};
    merged[name] = {
      ...existing,
      styleOverrides: {
        ...existing.styleOverrides,
        root: ringed(existing.styleOverrides?.root, override.styleOverrides.root as Record<string, unknown>),
      },
    };
  }
  return merged as Theme['components'];
}

const theme: Theme = { ...base, components: withFocusRing(base.components) };

export default theme;
